import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API } from "../lib/api";
import { MODELS, MODEL_KEYS, ModelChip, ModelBadge } from "../components/ModelBadge";
import Markdown from "../components/Markdown";
import { Send, Sparkles, Globe } from "lucide-react";
import ModelSettings from "../components/ModelSettings";
import { useModelSettings } from "../lib/settings";

export default function Chat() {
  const [params, setParams] = useSearchParams();
  const convId = params.get("c");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [model, setModel] = useState("deepseek");
  const [sending, setSending] = useState(false);
  const [streamBuf, setStreamBuf] = useState("");
  const [conversationId, setConversationId] = useState(convId || null);
  const [webSearch, setWebSearch] = useState(false);
  const [prompts, setPrompts] = useState({});
  const endRef = useRef(null);
  const abortRef = useRef(null);
  const { settings } = useModelSettings();

  useEffect(() => {
    api.get("/starter-prompts").then(({ data }) => setPrompts(data.prompts || {})).catch(() => {});
  }, []);

  useEffect(() => {
    if (convId) {
      (async () => {
        try {
          const { data } = await api.get(`/conversations/${convId}`);
          setMessages(data.messages || []);
          setConversationId(convId);
          if (data.conversation?.model) setModel(data.conversation.model);
        } catch {}
      })();
    } else {
      setMessages([]);
      setConversationId(null);
    }
  }, [convId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending, streamBuf]);

  const send = async (overridePrompt) => {
    const promptText = (overridePrompt ?? input).trim();
    if (!promptText || sending) return;
    setMessages((m) => [...m, { role: "user", content: promptText }]);
    setInput("");
    setSending(true);
    setStreamBuf("");

    const token = localStorage.getItem("token");
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          model,
          message: promptText,
          web_search: webSearch,
          temperature: settings[model]?.temperature,
          max_tokens: settings[model]?.max_tokens,
        }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) {
        const text = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${text.slice(0, 200)}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let acc = "";
      let metaConvId = null;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const ln of lines) {
          if (!ln.startsWith("data:")) continue;
          const payload = ln.slice(5).trim();
          if (!payload || payload === "[DONE]") continue;
          try {
            const ev = JSON.parse(payload);
            if (ev.type === "meta") {
              metaConvId = ev.conversation_id;
            } else if (ev.type === "delta") {
              acc += ev.content;
              setStreamBuf(acc);
            } else if (ev.type === "error") {
              acc = (acc ? acc + "\n\n" : "") + "⚠️ " + ev.error;
              setStreamBuf(acc);
            }
          } catch {}
        }
      }
      if (metaConvId && !conversationId) {
        setConversationId(metaConvId);
        // Defer setParams to next microtask to avoid setState-during-render warning
        queueMicrotask(() => setParams({ c: metaConvId }));
      }
      setMessages((m) => [...m, { role: "assistant", content: acc, model }]);
      setStreamBuf("");
      window.dispatchEvent(new Event("aimedley:conversations-changed"));
      // refresh sidebar title again ~3s later when auto-title is likely complete
      setTimeout(() => window.dispatchEvent(new Event("aimedley:conversations-changed")), 3500);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `⚠️ ${e.message}`, model, error: true }]);
      setStreamBuf("");
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  };

  const stop = () => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const m = MODELS[model];
  const modelPrompts = prompts[model] || [];

  return (
    <div className="flex flex-col h-screen" data-testid="chat-page">
      <header className="px-6 py-4 border-b-2 border-slate-900 bg-white flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold">Chat</h1>
          <p className="text-sm text-slate-500">One model at a time. Talk to your pick.</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center" data-testid="model-selector">
          {MODEL_KEYS.map((k) => (
            <ModelChip key={k} id={k} selected={model === k} onClick={() => setModel(k)} />
          ))}
          <ModelSettings mode="single" model={model} />
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-8" data-testid="messages-container">
        <div className="max-w-3xl mx-auto space-y-5">
          {messages.length === 0 && (
            <div className="text-center py-10">
              <div className="inline-block float-y mb-4">
                <Sparkles size={48} className="text-slate-300" strokeWidth={2} />
              </div>
              <div className="font-heading text-2xl font-bold mb-1">
                Say hi to <span style={{ color: m.color }}>{m.name}</span>
              </div>
              <div className="text-slate-500 mb-6">{m.personality}. Ask anything.</div>

              {modelPrompts.length > 0 && (
                <div className="max-w-xl mx-auto">
                  <div className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-3">
                    Try a starter prompt
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2" data-testid="starter-prompts">
                    {modelPrompts.map((p, i) => (
                      <button
                        key={i}
                        data-testid={`starter-prompt-${i}`}
                        onClick={() => send(p)}
                        className="text-left text-sm font-semibold px-3 py-2.5 rounded-xl border-2 transition-all hover:shadow-brut-sm"
                        style={{ borderColor: m.border, backgroundColor: m.bg + "55", color: "#0F172A" }}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {messages.map((msg, i) => (
            <ChatBubble key={i} msg={msg} />
          ))}
          {sending && (
            <div className="flex justify-start" data-testid="typing-indicator">
              <div
                className="border-2 rounded-2xl rounded-bl-sm px-4 py-3 max-w-[85%]"
                style={{ backgroundColor: m.bg, borderColor: m.border }}
              >
                <div className="flex items-center gap-1.5 text-xs font-bold mb-2" style={{ color: m.border }}>
                  <ModelBadge id={model} size="sm" />
                </div>
                {streamBuf ? (
                  <Markdown text={streamBuf} />
                ) : (
                  <div className="flex gap-1.5">
                    <span className="w-2 h-2 rounded-full dot-blink" style={{ backgroundColor: m.border }} />
                    <span className="w-2 h-2 rounded-full dot-blink" style={{ backgroundColor: m.border, animationDelay: "0.2s" }} />
                    <span className="w-2 h-2 rounded-full dot-blink" style={{ backgroundColor: m.border, animationDelay: "0.4s" }} />
                  </div>
                )}
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
      </div>

      <footer className="border-t-2 border-slate-900 bg-white p-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <button
              data-testid="web-search-toggle"
              onClick={() => setWebSearch((w) => !w)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-heading font-bold border-2 transition-all ${
                webSearch
                  ? "bg-emerald-500 text-white border-emerald-700 shadow-brut-sm"
                  : "bg-white text-slate-600 border-slate-300 hover:border-slate-900"
              }`}
            >
              <Globe size={14} strokeWidth={2.5} />
              Web search {webSearch ? "ON" : "OFF"}
            </button>
            {webSearch && (
              <span className="text-xs text-slate-500 font-medium">Model will search the web for fresh info.</span>
            )}
          </div>
          <div className="flex items-end gap-3">
            <textarea
              data-testid="chat-input"
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder={`Message ${m.name}…`}
              className="brut-input flex-1 resize-none max-h-32"
              style={{ minHeight: 48 }}
            />
            {sending ? (
              <button
                data-testid="chat-stop-btn"
                onClick={stop}
                className="brut-btn"
                style={{ backgroundColor: "#fff", color: "#0F172A" }}
              >
                Stop
              </button>
            ) : (
              <button
                data-testid="chat-send-btn"
                onClick={() => send()}
                disabled={!input.trim()}
                className="brut-btn"
                style={{ backgroundColor: m.color, borderColor: m.border }}
              >
                <Send size={16} strokeWidth={2.5} /> Send
              </button>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}

function ChatBubble({ msg }) {
  const isUser = msg.role === "user";
  const m = msg.model && MODELS[msg.model];
  if (isUser) {
    return (
      <div className="flex justify-end" data-testid="user-msg">
        <div className="max-w-[80%] bg-slate-900 text-white rounded-2xl rounded-br-sm px-4 py-3 font-medium">
          {msg.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start" data-testid={`assistant-msg-${msg.model || "agent"}`}>
      <div
        className="max-w-[85%] border-2 rounded-2xl rounded-bl-sm px-4 py-3"
        style={{
          backgroundColor: m?.bg || "#fff",
          borderColor: m?.border || "#0F172A",
        }}
      >
        {m && (
          <div className="flex items-center gap-1.5 text-xs font-bold mb-2 font-heading" style={{ color: m.border }}>
            <ModelBadge id={msg.model} size="sm" />
          </div>
        )}
        <Markdown text={msg.content} />
      </div>
    </div>
  );
}
